import { Injectable, signal } from '@angular/core';
import { DepartmentsFe, DepartmentsFields, DepartmentsForm, DepartmentsFormGroups, DepartmentsValidators, READONLY_BE_KEYS } from '../departments.model';
import { CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { Validators } from '@angular/forms';
import { Departments } from 'tilby-models';

@Injectable()
export class DepartmentDetailsFormService {
  
  private departmentForm!: DepartmentsFormGroups;
  departmentFormReady = signal(false);

  get form() {
    return this.departmentForm;
  }

  validators: DepartmentsValidators = {
    nameMaxLength: 20,
    lawRefMaxLength: 100,

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  };

  createForm(department: DepartmentsFields = new DepartmentsFe() ,hiddenFields?:Array<keyof DepartmentsFields>) {
    this.departmentForm = new CustomFormGroup<DepartmentsForm>({
      id: new CustomFormControl(
        department.id || null
      ),
      printer_code: new CustomFormControl(
        { value: department.printer_code || null, disabled: false }
      ),
      code: new CustomFormControl(
        department.code || null
      ),
      pms_code: new CustomFormControl(
        department.pms_code || null
      ),
      name: new CustomFormControl(
        department.name || null,
        { validators: [Validators.required, Validators.maxLength(this.validators.nameMaxLength)] }
      ),
      law_ref: new CustomFormControl(
        department.law_ref || null,
        { validators: [Validators.maxLength(this.validators.lawRefMaxLength)] }
      ),
      vat_id: new CustomFormControl(
        department.vat_id >= 0 ? department.vat_id : null,
        { validators: [Validators.required] }
      ),
      sales_type: new CustomFormControl(
        department.sales_type || null,
        { validators: [Validators.required] }
      ),
      activity_code_id: new CustomFormControl(
        department.activity_code_id || null
      ),
      giftcard_type_uuid: new CustomFormControl(
        department.giftcard_type_uuid || null
      ),
      not_discountable: new CustomFormControl(
        department.not_discountable || false
      ),
      display: new CustomFormControl(
        department.display || false
      )
    });

    this.departmentFormReady.set(true);
    return this.departmentForm;
  }
    
  resetFormValues(department: Departments = new DepartmentsFe()) {
    this.departmentForm.patchValue({
      id: department.id || null,
      printer_code: department.printer_code || null,
      code: department.code || null,
      pms_code: department.pms_code || null,
      name: department.name || null,
      law_ref: department.law_ref || null,
      vat_id: department.vat_id >= 0 ? department.vat_id : null,
      sales_type: department.sales_type || null,
      activity_code_id: department.activity_code_id || null,
      giftcard_type_uuid: department.giftcard_type_uuid || null,
      not_discountable: department.not_discountable || false,
      display: department.display || false
    });

    this.departmentForm.markAsPristine();
  }

  getDepartmentFields(currentDepartment: Departments = new DepartmentsFe()): Partial<Departments> {
    // Clono l'oggetto di destinazione per non mutare l'originale
    const updatedDepartment = { ...currentDepartment }; 

    // Recupero tutte le chiavi (nomi dei campi) presenti nel form
    const rawDepartmentToMap = Object.keys(this.departmentForm.controls);

    // Ciclo su tutte le chiavi trovate nel form
    rawDepartmentToMap.forEach((key: string) => {
      const control = this.departmentForm.get(key);

      if(control) {
        let value = control.value;

        if(key === 'vat_id' && value === 0) value = '0';

        if(key === 'giftcard_type_uuid' || typeof value === 'string') value = value || null;

        // Assegno il valore all'oggetto
        (updatedDepartment as any)[key] = value
      }
    })

    READONLY_BE_KEYS.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(updatedDepartment, key)) {
        delete (updatedDepartment as any)[key];
      }
    });

    return updatedDepartment;
  }

  get id() {
    return this.departmentForm.controls.id;
  }

  get printer_code() {
    return this.departmentForm.controls.printer_code;
  }

  get code() {
    return this.departmentForm.controls.code;
  }

  get pms_code() {
    return this.departmentForm.controls.pms_code;
  }

  get name() {
    return this.departmentForm.controls.name;
  }

  get law_ref() {
    return this.departmentForm.controls.law_ref;
  }

  get vat_id() {
    return this.departmentForm.controls.vat_id;
  }

  get sales_type() {
    return this.departmentForm.controls.sales_type;
  }

  get activity_code_id() {
    return this.departmentForm.controls.activity_code_id;
  }

  get giftcard_type_uuid() {
    return this.departmentForm.controls.giftcard_type_uuid;
  }

  get not_discountable() {
    return this.departmentForm.controls.not_discountable;
  }

  get display() {
    return this.departmentForm.controls.display;
  }
}
